/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */

import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { ZoneIndexesSelector, isAddNewZoneSelector, selectedZoneSelector, zoneDeviceListSelector } from "../../../store/api/deviceConfig/selectors";
import { IZoneDevice } from "../../../store/api/deviceConfig/types";
import { prepareZoneDevicePayload } from "../helpers";
import { selectedDeviceSelector } from "../../../store/api/devices/selectors";
import { OPERATIONTYPES } from "../../../globals/constants";
import { useSetDeviceConfigMutation } from "../../../store/api/deviceConfig/deviceConfigApiSlice";
import { useStoreDispatch } from "../../../store/hooks";
import { setBasicDialogOpenFor, setIsLoadingStatus, setOpenToast } from "../../../store/helpers/helperReducers";
import { setZoneDeviceList,setIsZoneAdded } from "../../../store/api/deviceConfig/reducers";
import { errorMessage, getMissingValues } from "../../../utils/helpers";
interface IPayload {
  [key: string]: string;
}

export const useZoneDeviceData = (selectedZoneDevice: IZoneDevice) => {
  const dispatch = useStoreDispatch();
  //const selectedZoneDevice = useSelector(selectedZoneSelector);
  const [zoneDeviceData, setZoneDeviceData] = useState<IZoneDevice>(selectedZoneDevice);
  const [errMsg, setErrMsg] = useState<IPayload>({});

  const [setDeviceConfig, configStatus] = useSetDeviceConfigMutation();

  let zoneDeviceList: IZoneDevice[] = useSelector(zoneDeviceListSelector);
  const isAddNewZoneDevice = useSelector(isAddNewZoneSelector);
  const selectedDevice = useSelector(selectedDeviceSelector);
  const zoneDeviceIndexes = useSelector(ZoneIndexesSelector);
  const nextZoneDeviceIndex = getMissingValues(zoneDeviceIndexes);

  useEffect(() => {
    dispatch(setIsLoadingStatus({ addZone: configStatus.isLoading }));
  }, [configStatus.isLoading, dispatch]);

  useEffect(() => {
    if (isAddNewZoneDevice) {
      setZoneDeviceData({
        ...selectedZoneDevice,
        index: nextZoneDeviceIndex,
      });
    } else if (selectedZoneDevice?.index) {
      setZoneDeviceData(selectedZoneDevice);
    }
  }, [isAddNewZoneDevice, nextZoneDeviceIndex, selectedZoneDevice]);

  const onBlurHandler = (event: any) => {
    const { name } = event?.target;
    if (name === "index") {
      if (zoneDeviceIndexes?.includes(zoneDeviceData?.index)) {
        setErrMsg({ ...errMsg, index: `Port ${zoneDeviceData?.index} is already in use.` });
        return;
      }
    }
  };

  const handleValidate = () => {
    let valid: any = true;
    if (zoneDeviceData?.index === "") {
      setErrMsg({ ...errMsg, index: `ID is required` });
      valid = false;
    }
    if (zoneDeviceData?.priority === "") {
      setErrMsg({ ...errMsg, priority: `Priority is required` });
      valid = false;
    }
    if (zoneDeviceData?.eARFCNUL === "") {
      setErrMsg({ ...errMsg, eARFCNUL: `EARFCNUL is required` });
      valid = false;
    }
    if (zoneDeviceData?.eARFCNDL === "") {
      setErrMsg({ ...errMsg, eARFCNDL: `EARFCNDL is required` });
      valid = false;
    }
    if (zoneDeviceData?.dlBandwidth === "") {
      setErrMsg({ ...errMsg, dlBandwidth: `DL bandwidth is required` });
      valid = false;
    }
    if (zoneDeviceData?.ulBandwidth === "") {
      setErrMsg({ ...errMsg, ulBandwidth: `UL bandwidth is required` });
      valid = false;
    }
    return valid;
  };

  const formatErrorMessage = (error: any) => {
    if (error?.data) {
      try {
        const errorData = typeof error.data === "string" ? JSON.parse(error.data) : error.data;
        return JSON.stringify(errorData, null, 2); // Format with indentation
      } catch (e) {
        return error.data;
      }
    }
    return errorMessage(error);
  };

  const submitHandler = async () => {
    if (!!zoneDeviceData) {
      const hasErros = Object.values(errMsg).some((err) => err !== "");
      if (handleValidate() && !hasErros) {
        const operationType = isAddNewZoneDevice ? OPERATIONTYPES.CREATE : OPERATIONTYPES.UPDATE;
        const zoneDevicePayload =
          !!zoneDeviceData &&
          !!selectedDevice?.objectKey &&
          prepareZoneDevicePayload(zoneDeviceData, selectedZoneDevice, selectedDevice?.objectKey, operationType, selectedDevice?.nodeType);
        try {
          let result: any;

          result = await setDeviceConfig(zoneDevicePayload);
          if (result?.error || configStatus?.isError) throw result?.error;
          if (result.data?.status === 200) {
            // if (!!zoneDeviceData) {
            //   zoneDeviceList = [zoneDeviceData, ...zoneDeviceList];
            // }
            dispatch(setIsZoneAdded(true));
            dispatch(setBasicDialogOpenFor(undefined));
            dispatch(
              setOpenToast({
                openToast: true,
                toastType: "success",
                message: `The data has been submited`,
              })
            );
            dispatch(setIsLoadingStatus({ addZone: false }));
            dispatch(setZoneDeviceList(zoneDeviceList));
          }
        } catch (error: any) {
          dispatch(setBasicDialogOpenFor(undefined));
          dispatch(setIsLoadingStatus({ addZone: false }));
          dispatch(setZoneDeviceList(zoneDeviceList));
          // dispatch(setOpenToast({ openToast: true, toastType: "error", message: error?.data ? JSON.stringify(error?.data) : errorMessage(error) }));
          const errorMsg = formatErrorMessage(error);
        dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMsg }));
        }
      }
    }
  };

  return {
    zoneDeviceData,
    setZoneDeviceData,
    isAddNewZoneDevice,
    submitHandler,
    onBlurHandler,
    errMsg,
    setErrMsg,
    selectedDevice,
    configStatus,
  };
};
